import { LIFE_SCIENCES_DATE_PROVIDERS } from '@my-backoffice/app/pages/life-sciences/shared/providers/life-sciences-date.providers';
import { MainComponent } from '@my-backoffice/app/pages/main/main.component';
import { FEDERATED_AUTH_ROUTES } from '@my-backoffice/app/shared/auth/login-routes.constants';
import { PageNotFoundComponent } from '@my-backoffice/app/shared/components/page-not-found/page-not-found.component';
import { AuthoritiesGuard } from '@my-backoffice/app/shared/guards/authorities/authorities.guard';
import { SwUpdateCheckGuard } from '@my-backoffice/app/shared/guards/sw-update-check/sw-update-check.guard';
import { TitleFaviconResolver } from '@my-backoffice/app/shared/resolvers/title-favicon-resolver.ts/title-favicon-resolver.service';
import { inject, NgModule, Type } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterModule, Routes } from '@angular/router';

const ROUTES: Routes = [
  {
    path: '',
    canActivate: [AuthoritiesGuard],
    component: MainComponent,
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      // HomeModule is kept for app customization; swap redirectTo for loadChildren when a hub page is needed.
      {
        path: 'home',
        redirectTo: 'life-sciences/studies',
        pathMatch: 'full',
      },
      {
        path: 'life-sciences',
        providers: LIFE_SCIENCES_DATE_PROVIDERS,
        children: [
          {
            path: '',
            redirectTo: 'studies',
            pathMatch: 'full',
          },
          {
            path: 'studies',
            title: TitleFaviconResolver,
            data: {
              altTitle: 'Life Sciences - Studies',
              routeKey: 'studies-list',
              reuseWhenFrom: ['create-study', 'study-detail'],
            },
            loadComponent: (): Promise<Type<unknown>> => import('./pages/life-sciences/studies-list/studies-list.component').then((m): Type<unknown> => m.StudiesListComponent),
          },
          {
            path: 'create-study',
            title: TitleFaviconResolver,
            data: {
              altTitle: 'Life Sciences - Create study',
              routeKey: 'create-study',
            },
            loadComponent: (): Promise<Type<unknown>> => import('./pages/life-sciences/create-study/create-study.component').then((m): Type<unknown> => m.CreateStudyComponent),
          },
          {
            path: 'study/:studyId',
            title: TitleFaviconResolver,
            data: {
              altTitle: 'Life Sciences - Study detail',
              routeKey: 'study-detail',
            },
            loadComponent: (): Promise<Type<unknown>> => import('./pages/life-sciences/study-detail/study-detail.component').then((m): Type<unknown> => m.StudyDetailComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'callback',
    redirectTo: 'auth/office365/callback',
  },
  {
    path: 'auth/office365/login',
    title: TitleFaviconResolver,
    loadComponent: (): Promise<Type<unknown>> =>
      import('./pages/login/federated/office365/pages/login-entry/office365-login-entry.component').then((m): Type<unknown> => m.Office365LoginEntryComponent),
  },
  {
    path: 'auth/office365/callback',
    title: TitleFaviconResolver,
    loadComponent: (): Promise<Type<unknown>> =>
      import('./pages/login/federated/office365/pages/login-callback/office365-login-callback.component').then((m): Type<unknown> => m.Office365LoginCallbackComponent),
  },
  {
    path: 'login/user/:userId/:userName/token/:token/:cacheType',
    canActivate: [
      (route: Readonly<ActivatedRouteSnapshot>): boolean => {
        const router = inject(Router);

        void router.navigate([FEDERATED_AUTH_ROUTES.office365.login], {
          queryParams: {
            fromBackOffice: true,
            userId: route.params.userId,
            userName: route.params.userName,
            token: route.params.token,
            cacheType: route.params.cacheType,
          },
          replaceUrl: true,
        });

        return false;
      },
    ],
    children: [],
  },
  {
    path: 'unauthorized',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Unauthorized',
    },
    loadComponent: (): Promise<Type<unknown>> => import('./pages/unauthorized/unauthorized.component').then((m): Type<unknown> => m.UnauthorizedComponent),
  },
  {
    path: '404',
    title: TitleFaviconResolver,
    data: {
      altTitle: '404',
      altFavicon: 'favicon2.ico',
    },
    component: PageNotFoundComponent,
  },
  {
    path: '**',
    canActivate: [SwUpdateCheckGuard],
    children: [],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(ROUTES, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
